import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { t } from '../i18n';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Bare from '../components/Bare';
import { analyticsAPI, advancedAPI, exportAPI, sheetAPI } from '../api';
import toast from 'react-hot-toast';

const PRESS_STATUS_COLOR = {
  working:   '#22c55e',
  idle:      'var(--dim)',
  retool:    '#f59e0b',
  breakdown: '#ef4444',
};

const PRESS_STATUS_LABEL = {
  working:   'Работает',
  idle:      'Простой',
  retool:    'Переналадка',
  breakdown: 'Неисправность',
};

const Card = ({ children, style = {} }) => (
  <div style={{ background: 'var(--card)', borderRadius: 12, padding: 20, ...style }}>
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 style={{
    color: 'var(--text)', fontSize: 14, fontWeight: 700, marginBottom: 16,
    textTransform: 'uppercase', letterSpacing: 0.8,
  }}>
    {children}
  </h2>
);

// ─── KPI карточка ────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, color = 'var(--accent)', icon }) {
  return (
    <div style={{
      background: 'var(--card)', borderRadius: 12, padding: '18px 20px',
      borderLeft: `4px solid ${color}`,
    }}>
      <div style={{ color: 'var(--dim)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
        {icon && <span style={{ marginRight: 6 }}>{icon}</span>}{label}
      </div>
      <div style={{ color: 'var(--text)', fontSize: 38, fontWeight: 900, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ color: 'var(--mid)', fontSize: 12, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ─── Круговой OEE ────────────────────────────────────────────────────────────
function OeeCircle({ value, size = 80 }) {
  const r    = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const pct  = Math.min(value, 100);
  const dash = (pct / 100) * circ;
  const col  = pct >= 75 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444';

  // Цифра стояла ВНУТРИ кольца — текстом в SVG, повёрнутым обратно на
  // 90°, потому что само кольцо повёрнуто на −90°. Поворот текста вокруг
  // своей середины браузеры считают по-разному: в части из них цифра
  // уезжала за край кольца или пропадала совсем, и на карточке оставался
  // пустой бублик. Теперь она обычным текстом слева от кольца — ни
  // поворотов, ни попыток уместить «100%» в 76 пикселей.
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
      <span style={{
        color: col, fontSize: size * 0.34, fontWeight: 900, lineHeight: 1,
        whiteSpace: 'nowrap',
      }}>{value}%</span>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bg)" strokeWidth={8} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth={8}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
      </svg>
    </div>
  );
}

// ─── Карточка пресса ─────────────────────────────────────────────────────────
function PressOeeCard({ press }) {
  const { oee } = press;
  const statusColor = PRESS_STATUS_COLOR[press.status] || 'var(--dim)';
  const statusLabel = PRESS_STATUS_LABEL[press.status] || press.status;

  const metrics = [
    { label: 'Доступность',      key: 'availability', color: 'var(--accent)' },
    {
      label: oee.performance_actual > 100
        ? `Производит. (факт ${oee.performance_actual}%)`
        : 'Производительность',
      key: 'performance',
      color: '#a855f7',
    },
    { label: 'Качество', key: 'quality', color: '#22c55e' },
  ];

  return (
    <Card style={{ border: `1px solid ${statusColor}30` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div>
          <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: 17 }}>⚙️ {press.name}</div>
          <div style={{ color: statusColor, fontSize: 12, fontWeight: 600, marginTop: 3, textTransform: 'uppercase' }}>
            {statusLabel}
            {press.current_texture && (
              <span style={{ color: 'var(--mid)', marginLeft: 8, fontWeight: 400 }}>— {press.current_texture}</span>
            )}
          </div>
        </div>
        <OeeCircle value={oee.oee_score} size={76} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
        {metrics.map(m => (
          <div key={m.key} style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
            <div style={{ color: m.color, fontSize: 22, fontWeight: 900 }}>{oee[m.key]}%</div>
            <div style={{ color: 'var(--mid)', fontSize: 11, marginTop: 2 }}>{m.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 12 }}>
        {[
          { label: 'Раб. время',  value: `${oee.working_hours} ч`,  color: '#22c55e' },
          { label: 'Простой',     value: `${oee.downtime_hours} ч`, color: '#ef4444' },
          { label: 'Произведено', value: `${press.produced} шт`,    color: 'var(--muted)' },
        ].map((s, i) => (
          <div key={i} style={{ background: 'var(--bg)', borderRadius: 6, padding: '7px 10px' }}>
            <div style={{ color: s.color, fontWeight: 700 }}>{s.value}</div>
            <div style={{ color: 'var(--mid)', fontSize: 11 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {press.defect > 0 && (
        <div style={{
          marginTop: 10, background: '#ef444410', border: '1px solid #ef444430',
          borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#ef4444', textAlign: 'center',
        }}>
          ⚠ Брак: {press.defect} шт
        </div>
      )}
    </Card>
  );
}

// ─── Пересортица на утверждении ──────────────────────────────────────────────
//
// Кладовщик перебивал сорт сам, и лист становился браком в ту же секунду.
// Брак — это деньги, списанные с выпуска, а решение принимал один человек, у
// которого стопка перед глазами и смена кончается. Теперь между «нашёл» и
// «стал браком» стоит директор.
//
// Пока он не решил, листы заморожены: ни в паллету, ни в отгрузку. Поэтому
// заявка не должна лежать здесь сутками — счётчик в меню горит красным ровно
// затем, чтобы её увидели.
function RegradeRequests({ onCount }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState({});
  const [zoom, setZoom] = useState(null);

  const load = useCallback(() => {
    sheetAPI.gradeRequests({ status: 'pending' })
      .then(r => {
        setRows(r.data.results || []);
        if (onCount) onCount(r.data.pending || 0);
      })
      .catch(() => setRows([]));
  }, [onCount]);

  useEffect(() => { load(); }, [load]);

  const decide = async (req, approve) => {
    setBusy(req.id);
    try {
      await sheetAPI.gradeRequestDecide(req.id, approve, note[req.id] || '');
      toast.success(approve
        ? `✅ Утверждено: ${req.count} листов → «${req.grade}»`
        : `↩ Отклонено: листы остались прежними`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Не удалось');
    } finally { setBusy(null); }
  };

  if (rows === null) return <Card><div style={{ color: 'var(--dim)' }}>Загрузка…</div></Card>;
  if (rows.length === 0) {
    return (
      <Card>
        <SectionTitle>🏷 {t('Пересортица на утверждении')}</SectionTitle>
        <div style={{ color: 'var(--dim)', fontSize: 14, padding: '10px 0' }}>
          Заявок нет — склад ничего не переводит.
        </div>
      </Card>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {zoom && (
        <div onClick={() => setZoom(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', zIndex: 1300,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <img src={zoom} alt="" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} />
        </div>
      )}

      {rows.map(r => (
        <Card key={r.id} style={{ border: '1px solid #f59e0b55' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={{ fontSize: 17, fontWeight: 800, color: 'var(--text)' }}>
              {r.count} {r.count === 1 ? 'лист' : 'листов'} → «{r.grade}»
            </span>
            <span style={{
              background: '#f59e0b20', color: '#f59e0b', borderRadius: 8,
              padding: '3px 10px', fontSize: 13, fontWeight: 700,
            }}>{r.reason}</span>
            <span style={{ color: 'var(--dim)', fontSize: 12.5, marginLeft: 'auto' }}>
              {r.by} · {r.created_at}
            </span>
          </div>

          {r.note && (
            <div style={{ color: 'var(--muted)', fontSize: 13.5, marginBottom: 10 }}>
              {r.note}
            </div>
          )}

          {r.photos.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {r.photos.map((u, i) => (
                <img key={i} src={u} alt="" onClick={() => setZoom(u)}
                  style={{
                    width: 96, height: 96, objectFit: 'cover', borderRadius: 10,
                    border: '1px solid var(--border)', cursor: 'zoom-in',
                  }} />
              ))}
            </div>
          )}

          <div style={{ overflowX: 'auto', marginBottom: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ color: 'var(--dim)', fontSize: 11.5 }}>
                  {['Бирка', 'Продукт', 'Сейчас', 'Пресс', 'Оператор', 'Принят'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '4px 8px', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {r.items.map(it => (
                  <tr key={it.sheet_id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '5px 8px', fontFamily: 'monospace', fontWeight: 700 }}>{it.code}</td>
                    <td style={{ padding: '5px 8px' }}>{it.product}</td>
                    <td style={{ padding: '5px 8px', color: 'var(--muted)' }}>{it.grade_from}</td>
                    <td style={{ padding: '5px 8px' }}>{it.press || '—'}</td>
                    <td style={{ padding: '5px 8px' }}>{it.operator || '—'}</td>
                    <td style={{ padding: '5px 8px', color: 'var(--dim)', whiteSpace: 'nowrap' }}>{it.accepted_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <input value={note[r.id] || ''}
            onChange={e => setNote(n => ({ ...n, [r.id]: e.target.value }))}
            placeholder="Что сказать складу — необязательно"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '9px 12px',
              background: 'var(--bg)', border: '1px solid var(--border)',
              borderRadius: 10, color: 'var(--text)', fontSize: 13,
              marginBottom: 10, fontFamily: 'inherit',
            }} />

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={() => decide(r, true)} disabled={busy === r.id}
              style={{
                flex: 1, minWidth: 180, padding: '13px 0', borderRadius: 11,
                background: '#22c55e', border: 'none', color: '#fff',
                fontSize: 15, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
              }}>
              {busy === r.id ? '…' : `✅ Утвердить — станут «${r.grade}»`}
            </button>
            <button onClick={() => decide(r, false)} disabled={busy === r.id}
              style={{
                flex: 1, minWidth: 180, padding: '13px 0', borderRadius: 11,
                background: 'var(--card)', border: '1px solid #ef4444',
                color: '#ef4444', fontSize: 15, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
              }}>
              ↩ Отклонить — останутся как есть
            </button>
          </div>
        </Card>
      ))}
    </div>
  );
}


// Круглый потолок шкалы: 5, 10, 20, 50, 100, 200, 500…
//
// Раньше шкалой был сам наибольший столбец, и он всегда занимал всю ширину.
// Три заказа в очереди рисовались так же, как триста: полоса до края, и
// разницу между «сегодня» и «за месяц» видно только по цифре сбоку. Круглый
// потолок возвращает картинке смысл — по длине полосы снова видно, много это
// или мало.
function niceMax(value) {
  const v = Math.max(Number(value) || 0, 1);
  if (v <= 5) return 5;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / pow;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * pow;
}


// ─── Диаграмма заказов ───────────────────────────────────────────────────────
function OrdersChart({ orders }) {
  // Статусов у заказа восемь, а показывали пять. «Ожидает согласования» и
  // «со склада — нужно разрешение» не попадали никуда — а это ровно те
  // заказы, что стоят и ждут решения человека. «Закрыт» тоже: заказ
  // отгружали полностью, и он исчезал с экрана.
  //
  // Пустые статусы не рисуем: полоса в ноль ничего не говорит, а места
  // занимает столько же.
  const bars = [
    { key: 'pending',    label: 'Ждёт согласования', color: '#ef4444' },
    { key: 'stock',      label: 'Со склада',         color: '#ef4444' },
    { key: 'queue',      label: 'В очереди',         color: '#f59e0b' },
    { key: 'production', label: 'В производстве',    color: 'var(--accent)' },
    { key: 'ready',      label: 'Готов',              color: '#22c55e' },
    { key: 'shipped',    label: 'Отгружен',           color: '#8b5cf6' },
    { key: 'closed',     label: 'Закрыт',             color: '#8b5cf6' },
    { key: 'cancelled',  label: 'Отменён',            color: 'var(--mid)' },
  ];

  // Полосы — по заказам, ВЗЯТЫМ за выбранный период. Раньше здесь стояло
  // состояние всего завода целиком, и переключатель «сегодня / 7 / 30 дней»
  // на диаграмму не влиял: за сегодня она показывала 346 готовых заказов,
  // накопленных за год. Карточки наверху так и остались «сейчас» — это
  // разные вопросы, и подписаны они теперь по-разному.
  const inPeriod = orders.period_by_status || {};
  const shown = bars.filter(b => (inPeriod[b.key] || 0) > 0);
  const max = niceMax(Math.max(...shown.map(b => inPeriod[b.key] || 0), 1));

  return (
    <Card>
      <SectionTitle>{t('📊 Статус заказов')}</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {shown.length === 0 && (
          <div style={{ color: 'var(--dim)', fontSize: 13 }}>
            За этот срок заказов не брали.
          </div>
        )}
        {shown.map(b => {
          const val = inPeriod[b.key] || 0;
          const pct = Math.round((val / max) * 100);
          return (
            <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 110, color: 'var(--dim)', fontSize: 12, textAlign: 'right', flexShrink: 0 }}>
                {b.label}
              </div>
              <div style={{ flex: 1, background: 'var(--bg)', borderRadius: 4, height: 22, position: 'relative' }}>
                <div style={{
                  width: `${pct}%`, height: 22, background: b.color, borderRadius: 4,
                  transition: 'width 0.5s ease', minWidth: val > 0 ? 8 : 0,
                }} />
                {val > 0 && (
                  <span style={{
                    position: 'absolute', left: `${pct}%`, top: '50%',
                    transform: 'translate(6px, -50%)', color: 'var(--text)', fontSize: 12, fontWeight: 700,
                  }}>{val}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{
        marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)',
        display: 'flex', gap: 24, fontSize: 13, flexWrap: 'wrap',
      }}>
        <span style={{ color: 'var(--dim)' }}>{t('Создано за период:')} <b style={{ color: '#22c55e' }}>{orders.period_created}</b></span>
        <span style={{ color: 'var(--dim)' }}>{t('Отгружено за период:')} <b style={{ color: '#8b5cf6' }}>{orders.period_shipped}</b></span>
        <span style={{ color: 'var(--dim)' }}>{t('Всего заказов в базе:')} <b style={{ color: 'var(--text)' }}>{orders.total}</b></span>
        {/* История статусов не ведётся: «сколько их было в очереди неделю
            назад» сказать нельзя. Поэтому полосы отвечают на то, что сказать
            можно честно, — что стало с заказами, взятыми за этот срок. */}
        <span style={{ color: 'var(--dim)', marginLeft: 'auto' }}>
          Полосы — заказы за период, в их нынешнем состоянии; шкала до {max}
        </span>
      </div>
    </Card>
  );
}

// ─── Предупреждения по сырью ─────────────────────────────────────────────────
function StockWarnings({ warnings }) {
  if (!warnings.length) return null;
  const critical    = warnings.filter(w => w.percent < 10);
  const borderColor = critical.length > 0 ? '#ef444440' : '#f59e0b30';
  const titleColor  = critical.length > 0 ? '#ef4444'   : '#f59e0b';

  return (
    <Card style={{ border: `1px solid ${borderColor}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <SectionTitle style={{ color: titleColor, marginBottom: 0 }}>
          {critical.length > 0 ? '🚨' : '⚠️'} Критический запас сырья
        </SectionTitle>
        <span style={{
          background: critical.length > 0 ? '#ef444420' : '#f59e0b20',
          color: critical.length > 0 ? '#ef4444' : '#f59e0b',
          borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700,
        }}>
          {warnings.length} позиций
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {warnings.map(w => {
          const isCrit = w.percent < 10;
          const color  = isCrit ? '#ef4444' : '#f59e0b';
          return (
            <div key={w.id} style={{
              background: isCrit ? '#ef444410' : 'var(--bg)',
              border: `1px solid ${isCrit ? '#ef444430' : 'transparent'}`,
              borderRadius: 8, padding: '10px 14px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, flexWrap: 'wrap', gap: 6 }}>
                <span style={{ color, fontSize: 13, fontWeight: isCrit ? 700 : 500 }}>
                  {isCrit ? '🔴' : '🟡'} {w.name}
                </span>
                <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
                  <span style={{ color: 'var(--dim)' }}>
                    Свободно: <b style={{ color }}>{w.available.toLocaleString()} {w.unit}</b>
                  </span>
                  {w.min_stock > 0 && (
                    <span style={{ color: 'var(--dim)' }}>
                      Минимум: <b style={{ color: 'var(--muted)' }}>{w.min_stock.toLocaleString()} {w.unit}</b>
                    </span>
                  )}
                  <span style={{ color: 'var(--dim)' }}>({w.percent}% от общего)</span>
                </div>
              </div>
              <div style={{ background: 'var(--card)', borderRadius: 4, height: 5 }}>
                <div style={{
                  width: `${Math.min(w.percent, 100)}%`, height: 5, borderRadius: 4,
                  background: color, transition: 'width 0.3s',
                }} />
              </div>
              {w.min_stock > 0 && w.available < w.min_stock && (
                <div style={{ color, fontSize: 11, marginTop: 3 }}>
                  Нужно закупить: ≈ {Math.ceil(w.min_stock - w.available).toLocaleString()} {w.unit}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── Расход материалов ───────────────────────────────────────────────────────
function MaterialConsumptionChart({ data }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.consumed), 1);
  return (
    <Card>
      <SectionTitle>{t('🧱 Расход материалов за период')}</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {data.map((row, i) => {
          const pct = Math.round((row.consumed / max) * 100);
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 160, color: 'var(--dim)', fontSize: 12, textAlign: 'right', flexShrink: 0, lineHeight: 1.3 }}>
                {row.name}
              </div>
              <div style={{ flex: 1, background: 'var(--bg)', borderRadius: 4, height: 22, position: 'relative' }}>
                <div style={{
                  width: `${pct}%`, height: 22, background: '#f59e0b',
                  borderRadius: 4, transition: 'width 0.5s ease', minWidth: pct > 0 ? 8 : 0,
                }} />
                {row.consumed > 0 && (
                  <span style={{
                    position: 'absolute', left: `max(${pct}%, 8px)`,
                    top: '50%', transform: 'translate(6px, -50%)',
                    color: 'var(--text)', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                  }}>
                    {row.consumed} {row.unit}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── Анализ брака по прессам ──────────────────────────────────────────────────
function GradeBreakdownChart({ data }) {
  if (!data || data.length === 0) return null;
  const GRADE_COLORS = { 'Брак': '#ef4444', '2-й сорт': '#f59e0b', '3-й сорт': '#8b5cf6' };
  return (
    <Card>
      <SectionTitle>{t('🔍 Анализ брака по прессам')}</SectionTitle>
      {data.map(pressRow => {
        const nonDefault = pressRow.grades.filter(g => !g.is_default);
        const total      = nonDefault.reduce((s, g) => s + g.total, 0);
        if (nonDefault.length === 0) return null;
        return (
          <div key={pressRow.press_name} style={{ marginBottom: 16 }}>
            <div style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
              ⚙️ {pressRow.press_name}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {nonDefault.map((g, i) => {
                const pct = total > 0 ? Math.round((g.total / total) * 100) : 0;
                const col = GRADE_COLORS[g.grade_name] || 'var(--accent)';
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 90, color: 'var(--dim)', fontSize: 12, textAlign: 'right' }}>
                      {g.grade_name}
                    </div>
                    <div style={{ flex: 1, background: 'var(--bg)', borderRadius: 4, height: 20, position: 'relative' }}>
                      <div style={{
                        width: `${pct}%`, height: 20, background: col,
                        borderRadius: 4, transition: 'width 0.5s ease', minWidth: 8,
                      }} />
                      <span style={{
                        position: 'absolute', left: `max(${pct}%, 10px)`,
                        top: '50%', transform: 'translate(6px,-50%)',
                        color: 'var(--text)', fontSize: 12, fontWeight: 700,
                      }}>
                        {g.total} шт
                      </span>
                    </div>
                  </div>
                );
              })}
              <div style={{ color: 'var(--mid)', fontSize: 11, textAlign: 'right' }}>
                Итого нестандарт: {total} шт
              </div>
            </div>
          </div>
        );
      })}
    </Card>
  );
}

// ─── Тренд производства по дням ───────────────────────────────────────────────
//
// Был столбик на день с датой, повёрнутой на тридцать градусов девятым
// кеглем: подпись налезала на соседнюю, и прочитать, какой это день, можно
// было только приблизившись к монитору. А вопрос к этому графику ровно один —
// «когда просело», — и ответ на него как раз в датах.
//
// Столбики заменены линией: выпуск по дням — это ход, а не набор отдельных
// величин, и провал в середине недели линия показывает сразу. Дата теперь
// стоит прямо, полностью и с днём недели: суббота проседает не потому, что
// что-то сломалось.
function DailyProductionChart({ data }) {
  if (!data || data.length === 0) return null;

  const n    = data.length;
  const qtys = data.map(d => d.qty);
  const max  = Math.max(...qtys, 1);
  const avg  = Math.round(qtys.reduce((a, b) => a + b, 0) / n);
  const maxI = qtys.indexOf(Math.max(...qtys));
  const minI = qtys.indexOf(Math.min(...qtys));

  // Точка по горизонтали и по вертикали в процентах — так линия тянется на
  // любую ширину, а подписи остаются прямыми и одного кегля.
  const px = (i) => (n === 1 ? 50 : (i / (n - 1)) * 100);
  const py = (v) => 100 - (v / max) * 88 - 6;

  // За тридцать дней все подписи не поместятся. Показываем каждую вторую или
  // каждую четвёртую, но лучший и худший день — всегда: за ними и приходят.
  const step  = n <= 10 ? 1 : n <= 20 ? 2 : 4;
  const isKey = (i) => (
    i === 0 || i === n - 1 || i === maxI || i === minI || i % step === 0);

  const line = data.map((r, i) => `${px(i)},${py(r.qty)}`).join(' ');
  const area = `0,100 ${line} 100,100`;

  const fmt = (iso, opts) =>
    new Date(iso + 'T00:00:00').toLocaleDateString('ru-RU', opts);

  return (
    <Card>
      <SectionTitle>{t('📈 Производство по дням')}</SectionTitle>

      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 14 }}>
        {[
          ['В среднем за день', avg + ' шт', 'var(--text)'],
          ['Лучший день', `${fmt(data[maxI].date, { day: '2-digit', month: '2-digit' })} — ${qtys[maxI]} шт`, '#22c55e'],
          ['Худший день', `${fmt(data[minI].date, { day: '2-digit', month: '2-digit' })} — ${qtys[minI]} шт`, '#f59e0b'],
        ].map(([k, v, c]) => (
          <div key={k}>
            <div style={{ color: 'var(--dim)', fontSize: 11.5 }}>{t(k)}</div>
            <div style={{ color: c, fontSize: 15, fontWeight: 800 }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Поля по краям — чтобы подписи крайних дней не срезались. */}
      <div style={{ margin: '0 26px' }}>
        <div style={{ position: 'relative', height: 150 }}>
          {/* Линия тянется по ширине: viewBox в процентах, растяжение по обеим
              осям. Толщину при этом не растягивает vector-effect, иначе на
              широком экране линия расплывалась бы в полосу. */}
          <svg viewBox="0 0 100 100" preserveAspectRatio="none"
               style={{ position: 'absolute', inset: 0, width: '100%', height: '100%',
                        overflow: 'visible' }}>
            <defs>
              <linearGradient id="dpc-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   style={{ stopColor: 'var(--accent)', stopOpacity: 0.28 }} />
                <stop offset="100%" style={{ stopColor: 'var(--accent)', stopOpacity: 0 }} />
              </linearGradient>
            </defs>
            {/* Средняя за период: провал виден не сам по себе, а по тому,
                насколько он ниже обычного дня. */}
            <line x1="0" y1={py(avg)} x2="100" y2={py(avg)}
                  strokeDasharray="4 4" vectorEffect="non-scaling-stroke"
                  style={{ stroke: 'var(--border)', strokeWidth: 1 }} />
            <polygon points={area} fill="url(#dpc-fill)" />
            <polyline points={line} fill="none" strokeLinejoin="round"
                      strokeLinecap="round" vectorEffect="non-scaling-stroke"
                      style={{ stroke: 'var(--accent)', strokeWidth: 2.5 }} />
          </svg>

          <div style={{
            position: 'absolute', right: 0, top: `${py(avg)}%`,
            transform: 'translate(0,-130%)', color: 'var(--dim)', fontSize: 11,
          }}>среднее {avg}</div>

          {data.map((r, i) => (
            <div key={i}>
              <div style={{
                position: 'absolute', left: `${px(i)}%`, top: `${py(r.qty)}%`,
                transform: 'translate(-50%,-50%)',
                width: i === maxI || i === minI ? 11 : 8,
                height: i === maxI || i === minI ? 11 : 8,
                borderRadius: '50%', background: 'var(--card)',
                border: `2.5px solid ${i === maxI ? '#22c55e' : i === minI ? '#f59e0b' : 'var(--accent)'}`,
              }} />
              {isKey(i) && (
                <div style={{
                  position: 'absolute', left: `${px(i)}%`, top: `${py(r.qty)}%`,
                  transform: 'translate(-50%,-170%)', whiteSpace: 'nowrap',
                  color: 'var(--text)', fontSize: 13, fontWeight: 800,
                }}>{r.qty}</div>
              )}
            </div>
          ))}
        </div>

        {/* Дата прямо и полностью. День недели рядом: воскресенье проседает
            не потому, что что-то случилось. */}
        <div style={{ position: 'relative', height: 38, marginTop: 8 }}>
          {data.map((r, i) => isKey(i) && (
            <div key={i} style={{
              position: 'absolute', left: `${px(i)}%`, transform: 'translateX(-50%)',
              textAlign: 'center', whiteSpace: 'nowrap',
            }}>
              <div style={{ color: 'var(--text)', fontSize: 13, fontWeight: 700 }}>
                {fmt(r.date, { day: '2-digit', month: '2-digit' })}
              </div>
              <div style={{ color: 'var(--dim)', fontSize: 11, marginTop: 1 }}>
                {fmt(r.date, { weekday: 'short' })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

// ─── Смена (сегодня) ─────────────────────────────────────────────────────────
// ─── Top Problems ────────────────────────────────────────────────────────────
function TopProblems({ problems }) {
  if (!problems) return null;

  if (problems.length === 0) {
    return (
      <div style={{
        background: '#22c55e12', border: '1px solid #22c55e30',
        borderRadius: 10, padding: '10px 16px', marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{ fontSize: 16 }}>✅</span>
        <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 14 }}>
          Всё в норме — проблем пока не найдено
        </span>
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--card)', borderRadius: 10, padding: '12px 16px',
      marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ color: 'var(--muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>
        ⚠️ Требуют внимания
      </div>
      {problems.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <span style={{ fontSize: 14, flexShrink: 0 }}>
            {p.severity === 'critical' ? '🔴' : '🟡'}
          </span>
          <div>
            <span style={{
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
              color: p.severity === 'critical' ? '#ef4444' : '#f59e0b',
              marginRight: 6,
            }}>
              [{p.severity}]
            </span>
            <span style={{ color: 'var(--text)', fontSize: 13 }}>{p.message}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Health Score ─────────────────────────────────────────────────────────────
// ─── Health Score Single (1 filial, doim ochiq) ───────────────────────────────
function HealthScoreSingle({ data }) {
  if (!data || data.length === 0) return null;
  const h = data[0];

  const scoreColor = h.score >= 80 ? '#22c55e' : h.score >= 60 ? '#f59e0b' : '#ef4444';
  const r = 44, circ = 2 * Math.PI * r;
  const dash = (Math.min(h.score, 100) / 100) * circ;

  const metrics = [
    {
      label: 'OEE',
      val:   h.oee,
      max:   100,
      color: h.oee >= 75 ? '#22c55e' : h.oee >= 50 ? '#f59e0b' : '#ef4444',
      fmt:   v => v + '%',
    },
    {
      label: 'Соблюдение сроков',
      val:   h.muddat_ijrosi,
      max:   100,
      color: h.muddat_ijrosi >= 80 ? '#22c55e' : h.muddat_ijrosi >= 60 ? '#f59e0b' : '#ef4444',
      fmt:   v => v + '%',
    },
    {
      label: 'Уровень брака',
      val:   Math.max(0, 100 - h.brak_foizi * 10),  // brak past = yaxshi → inverted bar
      max:   100,
      color: h.brak_foizi <= 2 ? '#22c55e' : h.brak_foizi <= 5 ? '#f59e0b' : '#ef4444',
      fmt:   () => h.brak_foizi + '%',
    },
    {
      label: 'Состояние сырья',
      val:   h.xomashyo_ball,
      max:   100,
      color: h.xomashyo_ball >= 80 ? '#22c55e' : h.xomashyo_ball >= 50 ? '#f59e0b' : '#ef4444',
      fmt:   v => v + ' ball',
    },
  ];

  return (
    <Card style={{ marginBottom: 16 }}>
      <SectionTitle>🏥 Здоровье завода</SectionTitle>
      <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>

        {/* Asosiy ball — katta OEE doirasi uslubida */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
          <svg width={100} height={100} style={{ transform: 'rotate(-90deg)' }}>
            <circle cx={50} cy={50} r={r} fill="none" stroke="var(--bg)" strokeWidth={10} />
            <circle cx={50} cy={50} r={r} fill="none" stroke={scoreColor} strokeWidth={10}
              strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
              style={{ transition: 'stroke-dasharray 0.6s ease' }}
            />
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle"
              fill={scoreColor} fontSize={22} fontWeight={900}
              style={{ transform: 'rotate(90deg)', transformOrigin: '50% 50%' }}>
              {h.score}
            </text>
          </svg>
          <div style={{ color: scoreColor, fontWeight: 700, fontSize: 12, marginTop: 4 }}>
            {h.score >= 80 ? '🟢 Yaxshi' : h.score >= 60 ? '🟡 O\'rta' : '🔴 Xavfli'}
          </div>
        </div>

        {/* 4 ta metrika — progress bar bilan */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 200 }}>
          {metrics.map(m => (
            <div key={m.label}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: 'var(--muted)', fontSize: 12 }}>{m.label}</span>
                <span style={{ color: m.color, fontWeight: 700, fontSize: 12 }}>{m.fmt(m.val)}</span>
              </div>
              <div style={{ background: 'var(--bg)', borderRadius: 4, height: 7 }}>
                <div style={{
                  width: `${Math.min(m.val, 100)}%`, height: 7,
                  background: m.color, borderRadius: 4,
                  transition: 'width 0.5s ease',
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

// ─── Defect Matrix ────────────────────────────────────────────────────────────
// Цех уходит от эталонного режима — и это не мелочь. На живых данных
// завода связь оказалась прямой: чем дальше от коридора, тем больше брака,
// без исключений. Поэтому экран начинается не с таблицы, а с одной фразы,
// после которой понятно, надо что-то делать или нет.
//
// Порог человек ставит сам. «Пять градусов» — не истина, а настройка, и
// увидеть, что даёт каждое значение, можно только попробовав. Поэтому он
// кнопками прямо здесь, а не в админке через три экрана.
// «Позиция», а не «смена». В базе это PressJob и код зовёт её сменой, но в
// цеху смена — это день или ночь, а здесь другое: один ЛДСП, одно
// количество, отпрессованное за один заход. Половина таких заходов длится
// восемнадцать минут, и на 361 позицию приходится 395 заходов — почти один
// к одному. Называть это сменой значило сбивать с толку в каждой строке.
// Клетка «низ / верх». Одна на эталон, на факт и на отклонение — читаются
// они вместе, и разный вид у трёх соседних столбцов сбивал бы сильнее, чем
// помогал.
//
// Порядок ВСЕГДА один: сперва низ, потом верх. Так это записано в
// справочнике («190/195» — низ 190, верх 195), так это читают в цехе, и
// подпись при каждом числе не нужна: место само говорит, чья цифра.
//
// Разбираемая плита — жирным, соседняя — серой. Вкладки делят их порознь, но
// смена одна, и вопрос у директора один: недогрет весь пресс или перекосило
// одну плиту. Пока вторая цифра лежала на соседней вкладке, ответить на это
// значило найти там ту же строку глазами.
function PlatePair({ plate, value, other, otherValue, color, bold, extra }) {
  if (value === undefined || value === null) return <span>—</span>;
  const mineIsBottom = plate !== 'верх';
  const bottom = mineIsBottom ? value : otherValue;
  const top    = mineIsBottom ? otherValue : value;
  const strong = { color: color || 'var(--text)', fontWeight: bold ? 700 : 400 };
  const weak   = { color: 'var(--dim)', fontWeight: 400 };
  const show = (v) => (v === undefined || v === null ? '—' : v);

  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      <span style={mineIsBottom ? strong : weak}>{show(bottom)}</span>
      <span style={{ color: 'var(--dim)' }}> / </span>
      <span style={mineIsBottom ? weak : strong}>{show(top)}</span>
      {extra}
    </span>
  );
}

// Знак несёт смысл: минус — недогрели, плюс — перегрели. Это разные поломки,
// и по модулю они выглядят одинаково.
const signed = (v) => (v === undefined || v === null)
  ? null : `${v > 0 ? '+' : ''}${v}`;

function ModeDeviation({ days }) {
  // Какая смена раскрыта до листов.
  //
  // «Здесь один брак» — и на этом разговор кончался: какой это лист, кто его
  // выпустил и в каком режиме, узнать было негде. А режим-то вот он, в этой
  // же строке: разговор о нём и начинается с листа.
  const [drill, setDrill] = useState(null);
  // Верх и низ — разные плиты: «190/195» в справочнике значит низ 190,
  // верх 195, а не коридор от 190 до 195. Допуск у каждой свой.
  const [tol, setTol]   = useState({
    temp_bottom: 5, temp_top: 5, hold: 2, pressure: 1 });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  // Три параметра в столбик — это экран на три прокрутки, и до давления
  // никто не доходит. Показываем по одному: выбрал — смотришь.
  const [tab, setTab]   = useState('temp_bottom');

  useEffect(() => {
    let alive = true;
    setBusy(true);
    advancedAPI.deviation({ days, ...tol })
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [days, tol]);

  const CARD = { background: 'var(--card)', borderRadius: 12, padding: 20 };
  if (!data) {
    return <div style={CARD}>
      <SectionTitle>🌡 {t('Отклонение от эталона')}</SectionTitle>
      <div style={{ color: 'var(--dim)', fontSize: 13 }}>
        {busy ? t('Считаем…') : t('Нет данных')}
      </div>
    </div>;
  }

  const STEPS = {
    temp_bottom: [2, 3, 5, 10], temp_top: [2, 3, 5, 10],
    hold: [1, 2, 3, 5], pressure: [0.5, 1, 2, 3] };

  // Ключ вкладки мог исчезнуть — например, «температура плит» разъехалась
  // на верх и низ. Пустой экран вместо отчёта тут ни к чему.
  const known = (data.params || []).some(p => p.key === tab);
  const shown = (data.params || []).filter(
    p => p.key === (known ? tab : (data.params[0] || {}).key));

  return (
    <div style={{ display: 'grid', gap: 16, opacity: busy ? 0.6 : 1 }}>
      {drill && (
        <DefectSheetsModal q={drill} days={days} press=""
          onClose={() => setDrill(null)} />
      )}
      <div style={{
        display: 'flex', gap: 8, flexWrap: 'wrap',
        position: 'sticky', top: 'var(--dir-head-h, 96px)', zIndex: 15,
        background: 'var(--bg)', padding: '8px 0',
      }}>
        {data.params.map(p => {
          const on = p.key === tab;
          // Рядом с названием — сразу разница. Иначе, чтобы понять, где
          // болит, пришлось бы открыть все три по очереди.
          return (
            <button key={p.key} onClick={() => setTab(p.key)} style={{
              background: on ? 'var(--accent)' : 'var(--card)',
              border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'),
              borderRadius: 10, color: on ? '#fff' : 'var(--muted)',
              padding: '9px 16px', fontSize: 13.5, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {p.title}
              {p.ratio && p.ratio > 1 && (
                <span style={{
                  background: on ? '#ffffff28' : '#ef444418',
                  color: on ? '#fff' : '#ef4444',
                  borderRadius: 6, padding: '1px 7px', fontSize: 11.5,
                }}>×{p.ratio}</span>
              )}
            </button>
          );
        })}
      </div>

      {shown.map(p => {
        const worse = p.ratio && p.ratio > 1;
        return (
          <div key={p.key} style={CARD}>
            <SectionTitle>🌡 {p.title} — {t('отклонение от эталона')}</SectionTitle>

            {/* Порог: пробуют и смотрят */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center',
                          flexWrap: 'wrap', marginBottom: 14 }}>
              <span style={{ color: 'var(--dim)', fontSize: 12.5 }}>
                {t('Считать отклонением от')}:
              </span>
              {STEPS[p.key].map(v => (
                <button key={v} onClick={() => setTol(x => ({ ...x, [p.key]: v }))}
                  style={{
                    background: p.tolerance === v ? 'var(--accent)' : 'var(--bg)',
                    border: '1px solid ' + (p.tolerance === v ? 'var(--accent)' : 'var(--border)'),
                    borderRadius: 8, color: p.tolerance === v ? '#fff' : 'var(--muted)',
                    padding: '5px 12px', fontSize: 12.5, fontWeight: 700,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}>{v}{p.unit}</button>
              ))}
              <input type="text" inputMode="decimal" defaultValue={p.tolerance}
                onKeyDown={e => {
                  if (e.key !== 'Enter') return;
                  const v = parseFloat(String(e.target.value).replace(',', '.'));
                  if (!isNaN(v) && v >= 0) setTol(x => ({ ...x, [p.key]: v }));
                }}
                title={t('Своё значение — и Enter')}
                style={{
                  width: 62, background: 'var(--bg)', border: '1px solid var(--border)',
                  borderRadius: 8, color: 'var(--text)', padding: '5px 8px',
                  fontSize: 12.5, textAlign: 'center', fontFamily: 'inherit',
                }} />
            </div>

            {/* Одна фраза, ради которой всё */}
            {p.compared === 0 ? (
              <div style={{ color: 'var(--dim)', fontSize: 13 }}>
                {t('Сравнить не с чем: эталон не заполнен')}
              </div>
            ) : (
              <div style={{
                padding: '14px 16px', borderRadius: 10, marginBottom: 14,
                background: worse ? '#ef444410' : 'var(--bg)',
                border: '1px solid ' + (worse ? '#ef444433' : 'var(--border)'),
              }}>
                <div style={{ fontSize: 15, color: 'var(--text)', lineHeight: 1.6 }}>
                  {t('Ушли от эталона на')} <b>{p.tolerance}{p.unit}</b> {t('и больше')}:
                  {' '}<b style={{ color: '#ef4444', fontSize: 19 }}>{p.outside.rate}%</b>
                  {' '}{t('некондиции')}.
                  {'  '}{t('В пределах')}:
                  {' '}<b style={{ color: '#22c55e', fontSize: 19 }}>{p.inside.rate}%</b>.
                  {p.ratio && <> {' — '}<b>{t('в')} {p.ratio} {t('раза больше')}</b></>}
                </div>
                <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 6 }}>
                  {p.outside.shifts} {t('позиций из')} {p.compared}
                  {' · '}{p.outside.sheets} {t('листов')}
                  {p.from_device > 0 && ' · со станка ' + p.from_device + ' позиций'}
                  {p.no_ref > 0 && ' · без эталона ' + p.no_ref + ' позиций'}
                </div>
              </div>
            )}

            {/* Чем дальше — тем хуже. Или нет: это тоже ответ */}
            {p.bands.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ color: 'var(--dim)', fontSize: 12 }}>
                    <th style={{ textAlign: 'left', padding: '4px 10px' }}>{t('Отклонение')}</th>
                    <th style={{ textAlign: 'right', padding: '4px 10px' }}>{t('Позиций')}</th>
                    <th style={{ textAlign: 'right', padding: '4px 10px' }}>{t('Листов')}</th>
                    <th style={{ textAlign: 'right', padding: '4px 10px' }}>{t('Некондиция')}</th>
                    <th style={{ textAlign: 'right', padding: '4px 10px' }}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {p.bands.map((b, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '6px 10px', color: 'var(--text)' }}>{b.title}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--muted)' }}>{b.shifts}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--muted)' }}>{b.sheets}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--muted)' }}>{b.defects}</td>
                      <td style={{
                        padding: '6px 10px', textAlign: 'right', fontWeight: 700,
                        color: b.rate > 2 ? '#ef4444' : b.rate > 1 ? '#f59e0b' : '#22c55e',
                      }}>{b.rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* А верен ли сам эталон */}
            {p.checks.length > 0 && (
              <div style={{ marginTop: 16, paddingTop: 14,
                            borderTop: '1px solid var(--border)' }}>
                <div style={{ color: 'var(--muted)', fontSize: 12.5, fontWeight: 700,
                              marginBottom: 8 }}>
                  {t('Цех работает не так, как записано в справочнике')}
                </div>
                {p.checks.map((c, i) => (
                  <div key={i} style={{ color: 'var(--text)', fontSize: 13,
                                        padding: '5px 0', lineHeight: 1.5 }}>
                    <b>{c.texture}</b>: {t('эталон')} {c.ref}{p.unit},
                    {' '}{t('по факту чаще')} <b>{c.typical}{p.unit}</b>
                    {' '}({c.side} {t('на')} {c.gap}{p.unit}),
                    {' '}{c.shifts} {t('позиций')}, {t('некондиция')} {c.rate}%
                  </div>
                ))}
                <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 6, lineHeight: 1.5 }}>
                  Все идут мимо коридора в одну сторону — значит либо справочник
                  устарел, либо цех не выходит на норму. Брак рядом показывает, что
                  из двух.
                </div>
              </div>
            )}

            {/* Кто и какие смены */}
            {(p.groups.press.length > 0 || p.shifts.length > 0) && (
              <div style={{ marginTop: 14 }}>
                <button onClick={() => setOpen(open === p.key ? null : p.key)}
                  style={{
                    background: 'var(--bg)', border: '1px solid var(--border)',
                    borderRadius: 8, color: 'var(--muted)', padding: '7px 14px',
                    fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}>
                  {open === p.key ? '▲ ' : '▼ '}{t('Кто отклоняется и какие позиции')}
                </button>

                {open === p.key && (
                  <div style={{ marginTop: 12 }}>
                    {['press', 'operator', 'texture', 'product'].map(gk => (
                      p.groups[gk].length > 0 && (
                        <div key={gk} style={{ marginBottom: 10 }}>
                          <div style={{ color: 'var(--dim)', fontSize: 11.5,
                                        textTransform: 'uppercase', marginBottom: 4 }}>
                            {{ press: 'Прессы', operator: 'Операторы',
                               texture: 'Матрицы', product: 'ЛДСП' }[gk]}
                          </div>
                          {p.groups[gk].map((g, i) => (
                            <div key={i} style={{ color: 'var(--text)', fontSize: 12.5,
                                                  padding: '2px 0' }}>
                              {g.name} — {g.outside} {t('из')} {g.shifts} {t('позиций')}
                              {' '}({g.share}%), {t('в среднем на')} {g.avg_gap}{p.unit}
                            </div>
                          ))}
                        </div>
                      )
                    ))}

                    <div style={{ overflowX: 'auto', marginTop: 8 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                        <thead>
                          <tr style={{ color: 'var(--dim)', fontSize: 11.5 }}>
                            {/* У температуры каждое из трёх чисел — пара плит,
                                и порядок в ней всегда «низ / верх». Сказано в
                                заголовке один раз, чтобы не подписывать каждую
                                цифру в каждой строке. */}
                            {['Дата', 'ЛДСП', 'Пресс', 'Оператор',
                              'Эталон', 'Факт', 'Отклонение',
                              'Листов', 'Некондиция'].map(h => (
                              <th key={h} style={{ textAlign: 'left', padding: '4px 8px' }}>
                                {t(h)}
                                {p.key.startsWith('temp')
                                  && ['Эталон', 'Факт', 'Отклонение'].includes(h) && (
                                  <span style={{ color: 'var(--dim)', fontWeight: 400 }}>
                                    {' '}низ / верх
                                  </span>
                                )}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {p.shifts.map((s, i) => (
                            <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                              <td style={{ padding: '5px 8px', color: 'var(--dim)' }}>
                                {s.started ? new Date(s.started).toLocaleDateString('ru-RU') : '—'}
                              </td>
                              <td style={{ padding: '5px 8px', color: 'var(--text)' }}>{s.product}</td>
                              <td style={{ padding: '5px 8px', color: 'var(--muted)' }}>{s.press}</td>
                              <td style={{ padding: '5px 8px', color: 'var(--muted)' }}>{s.operator}</td>
                              {/* Эталон, факт и отклонение — все три парой плит и
                                  одинаково. Отклонение здесь главное: директор
                                  спрашивает не «на сколько ушли», а «какая
                                  плита ушла». */}
                              <td style={{ padding: '5px 8px' }}>
                                <PlatePair plate={s.plate} value={s.ref}
                                  other={s.other_plate} otherValue={s.other_ref}
                                  color="var(--muted)" />
                              </td>
                              <td style={{ padding: '5px 8px' }}>
                                <PlatePair plate={s.plate} value={s.actual}
                                  other={s.other_plate} otherValue={s.other_actual}
                                  bold
                                  extra={/* Со станка или из головы — в споре о
                                            браке это разные по весу
                                            утверждения. */
                                    s.act_from === 'станок' ? (
                                      <span style={{
                                        marginLeft: 5, background: '#0ea5e920', color: '#0ea5e9',
                                        borderRadius: 5, padding: '0 5px', fontSize: 10,
                                        fontWeight: 600,
                                      }}>станок</span>
                                    ) : null} />
                              </td>
                              <td style={{ padding: '5px 8px' }}>
                                <PlatePair plate={s.plate}
                                  value={signed(s.gap)}
                                  other={s.other_plate}
                                  otherValue={signed(s.other_gap)}
                                  bold
                                  color={s.gap < 0 ? '#0ea5e9' : '#ef4444'} />
                                <span style={{ color: 'var(--dim)' }}>{p.unit}</span>
                              </td>
                              <td style={{ padding: '5px 8px', color: 'var(--muted)' }}>
                                {s.sheets}
                              </td>
                              {/* Нажимается: за цифрой всегда конкретные листы,
                                  и путь к ним должен быть один щелчок, а не
                                  переход в другой отчёт с теми же фильтрами,
                                  выставленными руками. */}
                              <td style={{ padding: '5px 8px' }}>
                                {s.defects > 0 ? (
                                  <button
                                    onClick={() => setDrill({ job: s.job_id })}
                                    title={t('Показать эти листы')}
                                    style={{
                                      background: 'none', border: 'none', padding: 0,
                                      cursor: 'pointer', fontFamily: 'inherit',
                                      fontSize: 'inherit', fontWeight: 700,
                                      color: '#ef4444', textDecoration: 'underline',
                                      textDecorationStyle: 'dotted',
                                    }}>
                                    {s.defects}
                                    <span style={{ color: 'var(--dim)', fontWeight: 400 }}>
                                      {' '}({s.rate}%)
                                    </span>
                                  </button>
                                ) : (
                                  <span style={{ color: 'var(--dim)' }}>—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Отчёт стоит ровно столько, сколько заполнено в справочнике */}
      {data.reference_fill && data.reference_fill.products_no_ref > 0 && (
        <div style={{ ...CARD, borderLeft: '3px solid #f59e0b' }}>
          <SectionTitle>📋 {t('Эталон заполнен не везде')}</SectionTitle>
          <div style={{ color: 'var(--text)', fontSize: 13.5, lineHeight: 1.6 }}>
            {t('ЛДСП без эталона')}: <b>{data.reference_fill.products_no_ref}</b>
            {' '}{t('из')} {data.reference_fill.products_total}.
            {' '}{t('Матриц без эталона')}: <b>{data.reference_fill.textures_no_ref}</b>
            {' '}{t('из')} {data.reference_fill.textures_total}.
          </div>
          <div style={{ color: 'var(--dim)', fontSize: 12.5, marginTop: 8, lineHeight: 1.5 }}>
            Пока у ЛДСП нет своего режима, смена сверяется с матрицей — один
            коридор на десяток разных плит. Работает, но грубо: чем больше
            заполнено, тем точнее разбор.
          </div>
          {data.reference_fill.textures_missing.length > 0 && (
            <div style={{ color: 'var(--muted)', fontSize: 12.5, marginTop: 8 }}>
              {t('Матрицы без эталона')}: {data.reference_fill.textures_missing.join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Брак виден числом: «за месяц 157 листов». С числом нельзя пойти в цех и
// что-то поправить — вопрос всегда один: ОТКУДА он взялся. Здесь листы
// разложены по каждому признаку сразу: партия ДСП, плёнка, пресс, смена,
// режим прессования. Где брака заметно больше среднего — то и наверху.
//
// Сначала короткий список «на что смотреть», потом разрезы целиком: директор
// открывает экран не изучать таблицы, а понять, куда идти.
// Листы, стоящие за цифрой.
//
// В отчёте написано «Пресс №3 — 3 не высших», и на этом разговор кончался:
// какие это три листа, кто их выпустил, когда и в каком режиме — узнать было
// негде. Директор видел число и не мог дойти до причины, а причина у брака
// всегда конкретная: этот лист, эта смена, эта плита.
function DefectSheetsModal({ q, days, press, onClose }) {
  const [rows, setRows] = useState(null);
  const [info, setInfo] = useState({});

  useEffect(() => {
    let alive = true;
    advancedAPI.defectSheets(q.job
      // Смена названа — ни период, ни пресс не нужны: она одна.
      ? { job: q.job, ...(q.grade ? { grade: q.grade } : {}) }
      : {
        days, dim: q.dim, value: q.value,
        ...(q.grade ? { grade: q.grade } : {}),
        ...(press ? { press } : {}),
      }).then(r => {
      if (!alive) return;
      setRows(r.data.rows || []);
      setInfo(r.data || {});
    }).catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [q, days, press]);

  const TH = {
    padding: '7px 10px', textAlign: 'left', fontSize: 11.5, fontWeight: 700,
    color: 'var(--dim)', textTransform: 'uppercase', whiteSpace: 'nowrap',
    borderBottom: '1px solid var(--border)', background: 'var(--card)',
    position: 'sticky', top: 0,
  };
  const TD = {
    padding: '7px 10px', fontSize: 12.5, color: 'var(--text)',
    borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--card)', borderRadius: 14, width: '100%', maxWidth: 1180,
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'var(--text)', fontSize: 15, fontWeight: 700 }}>
              {info.dimension_title || q.dim}: {q.value || '—'}
              {q.grade ? ` · ${q.grade}` : ''}
            </div>
            <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 3 }}>
              {rows === null ? 'Загрузка…'
                : `${info.count} листов за ${days} дн.` +
                  (info.count > info.shown ? ` · показаны первые ${info.shown}` : '')}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--muted)',
            fontSize: 22, cursor: 'pointer', lineHeight: 1,
          }}>✕</button>
        </div>

        <div style={{ overflow: 'auto', padding: '0 4px 8px' }}>
          {rows === null ? (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--dim)' }}>
              Загрузка…
            </div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--dim)' }}>
              Листов не нашлось
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>Бирка</th>
                  <th style={TH}>Сорт</th>
                  <th style={TH}>Причина</th>
                  <th style={TH}>Смена (день)</th>
                  <th style={TH}>Пресс</th>
                  <th style={TH}>Оператор</th>
                  <th style={TH}>Продукт</th>
                  {/* Режим ТОГО САМОГО листа: станок отдаёт показание на
                      каждое прессование. Откуда цифра — тоже написано:
                      «со станка» и «записал оператор» в разборе брака
                      весят по-разному. */}
                  <th style={TH}>Температура</th>
                  <th style={TH}>Выдержка</th>
                  <th style={TH}>Давление</th>
                  <th style={TH}>Плита / плёнка</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}>
                    <td style={{ ...TD, fontFamily: 'monospace', fontSize: 11.5 }}>
                      {String(r.code).slice(0, 12)}
                    </td>
                    <td style={{ ...TD, fontWeight: 700 }}>{r.grade}</td>
                    <td style={{ ...TD, color: r.reason ? 'var(--text)' : 'var(--dim)' }}>
                      {r.reason || 'не указана'}
                    </td>
                    <td style={TD}>
                      {r.day}
                      <span style={{ color: 'var(--dim)' }}> {r.shift}</span>
                      <div style={{ color: 'var(--dim)', fontSize: 11 }}>
                        принят {r.accepted_at}
                      </div>
                    </td>
                    <td style={TD}>{r.press || '—'}</td>
                    <td style={TD}>{r.operator || '—'}</td>
                    <td style={{ ...TD, whiteSpace: 'normal', minWidth: 190 }}>
                      {r.product}
                      {r.order && (
                        <div style={{ color: 'var(--dim)', fontSize: 11 }}>
                          {r.order} · {r.client}
                        </div>
                      )}
                    </td>
                    <td style={{ ...TD, fontWeight: 700 }}>
                      {r.temperature || '—'}
                      {r.mode_source === 'device' && (
                        <span title="Показание станка" style={{ color: '#22c55e' }}> ⚙</span>
                      )}
                    </td>
                    <td style={TD}>{r.hold || '—'}</td>
                    <td style={TD}>{r.pressure || '—'}</td>
                    <td style={{ ...TD, fontSize: 11.5, color: 'var(--muted)' }}>
                      {r.dsp_batch || '—'}
                      <div>{r.film_pallet || '—'}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{
          padding: '10px 20px', borderTop: '1px solid var(--border)',
          color: 'var(--dim)', fontSize: 11.5, lineHeight: 1.5,
        }}>
          ⚙ — режим снят станком на этом прессовании. Без значка цифру записал
          оператор при старте смены: она относится ко всей смене, а не к листу.
        </div>
      </div>
    </div>
  );
}

function DefectSources({ data, days, press }) {
  const [open, setOpen] = useState(null);
  // Какую цифру раскрыли: признак, группа и сорт
  const [drill, setDrill] = useState(null);
  if (!data || !data.total_sheets) return null;

  const dims = (data.dimensions || []).filter(d => d.rows.length > 0);
  const grades = data.grade_names || [];
  const flagged = [];
  dims.forEach(d => d.rows.forEach(r => {
    if (r.flagged) flagged.push({ ...r, dim: d.title });
  }));
  flagged.sort((a, b) => b.ratio - a.ratio);

  const CARD = { background: 'var(--card)', borderRadius: 12, padding: 20 };
  const rateColor = (r) => (r.flagged ? '#ef4444'
    : r.rate > data.overall_rate ? '#f59e0b' : '#22c55e');

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {drill && (
        <DefectSheetsModal q={drill} days={days} press={press}
          onClose={() => setDrill(null)} />
      )}
      <div style={CARD}>
        <SectionTitle>🔎 {t('Откуда берётся некондиция')}</SectionTitle>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 4 }}>
          {[
            ['Листов', data.total_sheets, 'var(--text)'],
            ['Некондиция', data.total_defects, '#f59e0b'],
            ['Средний уровень', data.overall_rate + '%', 'var(--accent)'],
            ['Со станка', data.from_device, 'var(--dim)'],
          ].map(([k, v, c]) => (
            <div key={k}>
              <div style={{ color: 'var(--dim)', fontSize: 12 }}>{t(k)}</div>
              <div style={{ color: c, fontSize: 20, fontWeight: 800 }}>{v}</div>
            </div>
          ))}
        </div>

        {/* Первый сорт и брак — разные новости. Первый, второй и третий
            продаются, дешевле, но продаются; брак не продаётся вовсе. Одной
            цифрой «брак» они означали «это выбросили», а решения за этими
            двумя вещами совсем разные. */}
        {(data.by_grade || []).length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
              {t('Сорт ЛДСП — как принял склад')}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {data.by_grade.map(g => (
                <span key={g.name} style={{
                  background: 'var(--bg)', border: '1px solid var(--border)',
                  borderRadius: 8, padding: '5px 11px', fontSize: 12.5,
                  color: 'var(--muted)',
                }}>
                  {g.name}: <b style={{ color: 'var(--text)' }}>{g.count}</b>
                  <span style={{ color: 'var(--dim)' }}> · {g.share}%</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Разбор выше построен на листах, и потому не видит двух вещей.

            Плёнки в нём нет вовсе: её бракуют ДО пресса, листа из неё не
            вышло — это чистая потеря материала, и стоит она отдельно.

            А счёт пресса — те же самые листы, что склад разнёс по сортам:
            оператор говорит СКОЛЬКО вышло не высшими, склад решает КАКОГО
            они сорта. За неделю пресс насчитал 46, склад поставил ровно 46
            первых сортов. Складывать нельзя — получится вдвое. */}
        {(data.press_marks || []).length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
              {t('Отметил оператор у пресса')}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {data.press_marks.map(m => (
                <span key={m.name} style={{
                  background: 'var(--bg)',
                  border: '1px solid ' + (m.material ? '#f59e0b55' : 'var(--border)'),
                  borderRadius: 8, padding: '5px 11px', fontSize: 12.5,
                  color: 'var(--muted)',
                }}>
                  {m.name}: <b style={{ color: 'var(--text)' }}>{m.count}</b>
                </span>
              ))}
            </div>
            <div style={{ color: 'var(--dim)', fontSize: 11.5, marginTop: 6, lineHeight: 1.5 }}>
              «Не 1-й сорт» — предварительный счёт оператора: это те же листы,
              которым сорт поставил склад, а не добавка к ним. Плёнку бракуют
              до пресса — листа из неё не вышло, в выпуск она не попадает.
            </div>
          </div>
        )}
        <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 10, lineHeight: 1.5 }}>
          Группы меньше {data.min_sheets} листов не показываются: три брака из
          пяти дают 60% и возглавляют любой список, ничего при этом не значая.
          Отмечено — где брака вдвое больше среднего и набралось хотя бы три листа.
        </div>
      </div>

      {flagged.length > 0 && (
        <div style={CARD}>
          <SectionTitle>⚑ {t('На что смотреть')}</SectionTitle>
          {flagged.slice(0, 8).map((r, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              padding: '10px 12px', marginBottom: 8, borderRadius: 10,
              background: '#ef444410', border: '1px solid #ef444433',
            }}>
              <div style={{ minWidth: 150 }}>
                <div style={{ color: 'var(--dim)', fontSize: 11 }}>{r.dim}</div>
                <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 700 }}>{r.name}</div>
              </div>
              <div style={{ color: '#ef4444', fontSize: 18, fontWeight: 900 }}>{r.rate}%</div>
              <div style={{ color: 'var(--muted)', fontSize: 12.5 }}>
                {r.defects} {t('из')} {r.sheets} {t('листов')}
                {r.ratio ? ' · в ' + r.ratio + ' раза хуже среднего' : ''}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={CARD}>
        <SectionTitle>📐 {t('Разрезы')}</SectionTitle>
        <div style={{ display: 'grid', gap: 8 }}>
          {dims.map(d => (
            <div key={d.key}>
              <button onClick={() => setOpen(open === d.key ? null : d.key)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  background: 'var(--bg)', border: '1px solid var(--border)',
                  borderRadius: 10, padding: '10px 14px', cursor: 'pointer',
                  fontFamily: 'inherit', textAlign: 'left',
                }}>
                <span style={{ color: 'var(--text)', fontSize: 13.5, fontWeight: 700, flex: 1 }}>
                  {d.title}
                </span>
                {d.flagged > 0 && (
                  <span style={{
                    background: '#ef444420', color: '#ef4444', borderRadius: 6,
                    padding: '1px 8px', fontSize: 11.5, fontWeight: 700,
                  }}>⚑ {d.flagged}</span>
                )}
                <span style={{ color: 'var(--dim)', fontSize: 12 }}>
                  {d.rows.length}{d.hidden ? ' (+' + d.hidden + ' мелких)' : ''}
                </span>
                <span style={{ color: 'var(--dim)', fontSize: 12 }}>
                  {open === d.key ? '▲' : '▼'}
                </span>
              </button>

              {open === d.key && (
                <div style={{ overflowX: 'auto', padding: '8px 4px 4px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: 'var(--dim)', fontSize: 12 }}>
                        <th style={{ textAlign: 'left', padding: '4px 10px' }}>{d.title}</th>
                        <th style={{ textAlign: 'right', padding: '4px 10px' }}>{t('Листов')}</th>
                        {/* Колонка на каждый сорт: первый продаётся, брак нет.
                            Порядок один на весь отчёт, чтобы глаз не искал
                            заново в каждой таблице. */}
                        {grades.map(g => (
                          <th key={g} style={{ textAlign: 'right', padding: '4px 10px' }}>{g}</th>
                        ))}
                        <th style={{ textAlign: 'right', padding: '4px 10px' }}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.rows.map((r, i) => (
                        <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                          <td style={{ padding: '6px 10px', color: 'var(--text)' }}>
                            {r.flagged ? '⚑ ' : ''}{r.name}
                          </td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--muted)' }}>
                            {r.sheets}
                          </td>
                          {grades.map(g => {
                            const n = (r.grades || {})[g];
                            return (
                              <td key={g} style={{
                                padding: '6px 10px', textAlign: 'right',
                                color: n ? 'var(--text)' : 'var(--dim)',
                                fontWeight: n ? 700 : 400,
                              }}>
                                {n ? (
                                  // За цифрой стоят конкретные листы — по
                                  // клику видно какие: кто, когда, в каком
                                  // режиме. Раньше отчёт кончался на числе.
                                  <span
                                    onClick={() => setDrill({ dim: d.key, value: r.name, grade: g })}
                                    title="Показать листы"
                                    style={{
                                      cursor: 'pointer', color: 'var(--accent)',
                                      borderBottom: '1px dashed var(--accent)',
                                    }}>{n}</span>
                                ) : '—'}
                              </td>
                            );
                          })}
                          <td style={{
                            padding: '6px 10px', textAlign: 'right',
                            color: rateColor(r), fontWeight: 700,
                          }}>
                            <span
                              onClick={() => setDrill({ dim: d.key, value: r.name, grade: '' })}
                              title="Показать всю некондицию этой группы"
                              style={{ cursor: 'pointer', borderBottom: '1px dashed currentColor' }}
                            >{r.rate}%</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DefectMatrix({ matrix }) {
  if (!matrix || matrix.length === 0) return null;

  // Noyob presslar va operatorlar
  const presses   = [...new Set(matrix.map(r => r.press_name))].sort();
  const operators = [...new Set(matrix.map(r => r.operator_name))].sort();

  // Tezkor qidirish uchun map
  const lookup = {};
  let   maxRate = 0;
  matrix.forEach(r => {
    lookup[`${r.operator_name}||${r.press_name}`] = r;
    if (r.defect_rate > maxRate) maxRate = r.defect_rate;
  });

  const cellBg = (rate) => {
    if (rate === undefined) return 'transparent';
    if (rate > 5) return '#ef444418';
    if (rate > 3) return '#f59e0b12';
    return 'transparent';
  };
  const cellColor = (rate) => {
    if (rate === undefined) return 'var(--dim)';
    if (rate > 5) return '#ef4444';
    if (rate > 3) return '#f59e0b';
    return '#22c55e';
  };

  return (
    <div style={{ background: 'var(--card)', borderRadius: 12, padding: 20 }}>
      <SectionTitle>🧮 Матрица брака — пресс × оператор</SectionTitle>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ color: 'var(--dim)', padding: '6px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12, borderBottom: '1px solid var(--border)' }}>
                Оператор
              </th>
              {presses.map(p => (
                <th key={p} style={{ color: 'var(--muted)', padding: '6px 12px', textAlign: 'center', fontWeight: 600, fontSize: 12, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                  {p}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {operators.map(op => (
              <tr key={op}>
                <td style={{ color: 'var(--text)', padding: '8px 12px', fontWeight: 600, fontSize: 13, borderBottom: '1px solid var(--border)20' }}>
                  {op}
                </td>
                {presses.map(pr => {
                  const cell = lookup[`${op}||${pr}`];
                  const rate = cell?.defect_rate;
                  const isWorst = rate !== undefined && rate === maxRate && maxRate > 3;
                  return (
                    <td key={pr} style={{
                      padding: '8px 12px', textAlign: 'center',
                      background: isWorst ? '#ef444428' : cellBg(rate),
                      border: isWorst ? '2px solid #ef444450' : '1px solid transparent',
                      borderBottom: '1px solid var(--border)20',
                      borderRadius: isWorst ? 6 : 0,
                    }}>
                      {rate !== undefined ? (
                        <span style={{ color: cellColor(rate), fontWeight: 700 }}>
                          {rate.toFixed(1)}%
                          {rate > 5 ? ' 🔴' : rate > 3 ? ' 🟡' : ''}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--border)' }}>—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Shift Comparison ─────────────────────────────────────────────────────────
function ShiftComparison({ shifts }) {
  if (!shifts || shifts.length === 0) return null;

  const normColor = (n) => n >= 100 ? '#22c55e' : n >= 90 ? '#f59e0b' : '#ef4444';
  const normIcon  = (n) => n >= 100 ? '🟢' : n >= 90 ? '🟡' : '🔴';

  return (
    <div style={{ background: 'var(--card)', borderRadius: 12, padding: 20 }}>
      <SectionTitle>🕐 Сравнение смен</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {shifts.map(s => {
          const norm  = s.norm_fulfillment;
          const nCol  = normColor(norm);
          const barW  = Math.min(norm, 130);   // 100% = asosiy kenglik, >100% ozgina chiqadi
          return (
            <div key={s.shift} style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
                <span style={{ color: 'var(--text)', fontWeight: 700, fontSize: 14, minWidth: 90 }}>{s.shift}</span>
                <div style={{ display: 'flex', gap: 20, fontSize: 13, flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--muted)' }}>
                    <b style={{ color: 'var(--text)' }}>{s.total_done.toLocaleString('ru')}</b> шт
                  </span>
                  <span style={{ color: 'var(--dim)' }}>
                    брак: <b style={{ color: s.defect_rate > 5 ? '#ef4444' : s.defect_rate > 3 ? '#f59e0b' : '#22c55e' }}>
                      {s.defect_rate.toFixed(1)}%
                    </b>
                  </span>
                  <span style={{ color: nCol, fontWeight: 700 }}>
                    норма {norm.toFixed(0)}% {normIcon(norm)}
                  </span>
                </div>
              </div>
              {/* Полоса: 100% — план, выше — перевыполнение */}
              <div style={{ background: 'var(--card)', borderRadius: 4, height: 6, position: 'relative', overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.min(barW, 100)}%`, height: 6,
                  background: nCol, borderRadius: 4, transition: 'width 0.4s',
                  opacity: norm > 100 ? 1 : 0.85,
                }} />
                {/* 100% belgisi */}
                <div style={{ position: 'absolute', top: 0, left: '76.9%', width: 1, height: 6, background: 'var(--border)' }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Capacity Forecast ────────────────────────────────────────────────────────
function CapacityForecast({ forecast }) {
  if (!forecast) return null;
  const { daily_avg_output, days_of_work, xomashyo_tugash, risky_orders } = forecast;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {/* Ish hajmi */}
      <div style={{ background: 'var(--card)', borderRadius: 12, padding: 20 }}>
        <SectionTitle>📈 Прогноз производства</SectionTitle>
        <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
          <div style={{ flex: 1, background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
            <div style={{ color: 'var(--dim)', fontSize: 11, marginBottom: 4 }}>{t('В среднем за день')}</div>
            <div style={{ color: 'var(--text)', fontSize: 28, fontWeight: 900 }}>
              {daily_avg_output?.toLocaleString('ru') || '—'}
            </div>
            <div style={{ color: 'var(--dim)', fontSize: 11 }}>{t('шт/день')}</div>
          </div>
          <div style={{ flex: 1, background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
            <div style={{ color: 'var(--dim)', fontSize: 11, marginBottom: 4 }}>{t('Работы в очереди')}</div>
            <div style={{ color: days_of_work > 30 ? '#ef4444' : days_of_work > 14 ? '#f59e0b' : '#22c55e', fontSize: 28, fontWeight: 900 }}>
              {days_of_work ?? '—'}
            </div>
            <div style={{ color: 'var(--dim)', fontSize: 11 }}>дней</div>
          </div>
        </div>

        {/* Риск нехватки сырья */}
        {xomashyo_tugash?.length > 0 && (
          <>
            <div style={{ color: '#f59e0b', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
              ⚠️ Риск нехватки сырья:
            </div>
            {xomashyo_tugash.slice(0, 4).map((x, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0', borderBottom: '1px solid var(--border)20' }}>
                <span style={{ color: 'var(--muted)' }}>{x.nomenclature}</span>
                <span style={{ color: x.days_left < 5 ? '#ef4444' : '#f59e0b', fontWeight: 700 }}>
                  → {x.days_left} дн.
                </span>
              </div>
            ))}
          </>
        )}
        {(!xomashyo_tugash || xomashyo_tugash.length === 0) && (
          <div style={{ color: '#22c55e', fontSize: 12 }}>✅ Сырья достаточно</div>
        )}
      </div>

      {/* Заказы под угрозой срока */}
      <div style={{ background: 'var(--card)', borderRadius: 12, padding: 20 }}>
        <SectionTitle>🚨 Заказы под угрозой срока</SectionTitle>
        {risky_orders?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {risky_orders.map((o, i) => (
              <div key={i} style={{
                background: o.days_left <= 0 ? '#ef444415' : o.days_left <= 2 ? '#ef444410' : 'var(--bg)',
                border: `1px solid ${o.days_left <= 2 ? '#ef444430' : 'var(--border)20'}`,
                borderRadius: 8, padding: '8px 12px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <span style={{ color: 'var(--text)', fontWeight: 700, fontSize: 13 }}>{o.order_number}</span>
                  <span style={{ color: 'var(--dim)', fontSize: 12, marginLeft: 8 }}>{o.client}</span>
                </div>
                <span style={{
                  color: o.days_left <= 0 ? '#ef4444' : o.days_left <= 2 ? '#ef4444' : '#f59e0b',
                  fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap',
                }}>
                  {o.days_left <= 0 ? `просрочен на ${-o.days_left} дн.` : `осталось ${o.days_left} дн.`}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#22c55e', fontSize: 13, textAlign: 'center', padding: 20 }}>
            ✅ Заказов под угрозой срока нет
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Supplier Quality ─────────────────────────────────────────────────────────
function SupplierQuality({ suppliers }) {
  if (!suppliers) return null;

  const stars = (rate) => {
    const n = rate < 2 ? 5 : rate < 3 ? 4 : rate < 4 ? 3 : rate < 5 ? 2 : 1;
    return '⭐'.repeat(n) + '☆'.repeat(5 - n);
  };
  const starColor = (rate) => rate < 3 ? '#22c55e' : rate < 5 ? '#f59e0b' : '#ef4444';

  // Eng yomoni tepada
  const sorted = [...(suppliers || [])].sort((a, b) => b.defect_rate - a.defect_rate);

  return (
    <div style={{ background: 'var(--card)', borderRadius: 12, padding: 20 }}>
      <SectionTitle>🏭 Качество поставщиков</SectionTitle>
      {sorted.length === 0 ? (
        <div style={{ color: 'var(--dim)', fontSize: 13, fontStyle: 'italic', textAlign: 'center', padding: 16 }}>
          📊 Данные собираются — партии сырья пока не связаны
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sorted.map((s, i) => {
            const rate   = s.defect_rate;
            const col    = starColor(rate);
            const isBad  = rate >= 5;
            return (
              <div key={i} style={{
                background: isBad ? '#ef444410' : 'var(--bg)',
                border: `1px solid ${isBad ? '#ef444430' : 'transparent'}`,
                borderRadius: 8, padding: '10px 14px',
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              }}>
                <span style={{ color: 'var(--text)', fontWeight: 600, fontSize: 14, minWidth: 160 }}>
                  {s.supplier_name}
                </span>
                <span style={{ fontSize: 14, letterSpacing: -1 }} title={`Рейтинг: ${5 - (rate < 2 ? 0 : rate < 3 ? 1 : rate < 4 ? 2 : rate < 5 ? 3 : 4)} из 5`}>
                  {stars(rate)}
                </span>
                <span style={{ color: col, fontWeight: 700, fontSize: 13 }}>
                  брак {rate.toFixed(1)}%
                  {isBad ? ' 🔴' : rate >= 3 ? ' 🟡' : ''}
                </span>
                <span style={{ color: 'var(--dim)', fontSize: 12, marginLeft: 'auto' }}>
                  {s.batch_count} партий
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Branch Comparison ────────────────────────────────────────────────────────
// Сводка смены — короткая строка про сегодня. Живёт здесь, а показывается
// на «Времени прессов»: там разбирают, куда ушёл сегодняшний день, и первый
// вопрос к такому разбору — сколько при этом сделали.
export function ShiftSummary() {
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    analyticsAPI.getDashboard(1).then(r => setD(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 120000); return () => clearInterval(t); }, [load]);

  if (!d) return null;
  const prod    = d.production;
  const defRate = prod.defect_rate || 0;

  return (
    <div style={{
      background: 'var(--card)', borderRadius: 12, padding: '14px 20px', marginBottom: 20,
      border: '1px solid var(--border)',
      display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center',
    }}>
      <span style={{ color: 'var(--dim)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, flexShrink: 0 }}>
        📅 Смена (сегодня)
      </span>
      {[
        { label: 'Произведено',    value: prod.total_done,     unit: 'шт',  color: '#22c55e' },
        { label: 'Запланировано',  value: prod.total_planned,  unit: 'шт',  color: 'var(--accent)' },
        { label: 'Брак',           value: prod.total_defect,   unit: `шт (${defRate}%)`, color: defRate > 2 ? '#ef4444' : 'var(--dim)' },
        { label: 'Заказов готово', value: d.orders.ready,      unit: 'шт',  color: '#22c55e' },
        { label: 'В производстве', value: d.orders.production, unit: 'шт',  color: '#f59e0b' },
      ].map((s, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ color: s.color, fontSize: 22, fontWeight: 900 }}>{s.value}</span>
          <span style={{ color: 'var(--mid)', fontSize: 11 }}>{s.unit}</span>
          <span style={{ color: 'var(--border)', fontSize: 11 }}>{s.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Главная страница директора ───────────────────────────────────────────────
//
// Девятнадцать блоков шли одной лентой, и до сравнения смен приходилось
// прокручивать полторы тысячи пикселей. Директор смотрит панель между делом:
// либо он видит нужное сразу, либо не смотрит вовсе.
//
// Разделы собраны по вопросу, на который отвечают: «что сейчас», «сколько
// сделали», «как работают прессы», «что с качеством», «что будет дальше».
// Панель периода и выгрузки стоит над разделами — она нужна в любом из них.
// Разделы этой страницы стоят в БОКОВОМ меню, а список их живёт в
// DirectorHub. Раньше они были полосой кнопок поверх содержимого, и получалось
// три этажа: меню → «Разбор по цифрам» → семь вкладок. Директор искал нужное
// на третьем этаже, а с бокового меню видел только два слова.
//
// Теперь раздел приходит сверху пропсом: страница его не выбирает, а
// показывает. Один список названий — в DirectorHub, и в поиске по программе
// (nav.js) он же.
export default function DirectorDashboard({ bare, onOpen, section: ctrlSection,
                                            onSection: ctrlOnSection,
                                            onRegradeCount, initialSection }) {
  // Внутри общего дашборда директора страница рисуется без своей шапки и меню
  const Shell = bare ? Bare : Layout;

  const navigate                            = useNavigate();
  const [period, setPeriod]                 = useState(7);
  // Своё состояние — только на случай, когда страницу открыли саму по себе.
  // Внутри дашборда разделом управляет тот, кто её вложил.
  const [ownSection, setOwnSection]         = useState(initialSection || 'orders');
  const section    = ctrlSection === undefined ? ownSection : ctrlSection;
  const setSection = ctrlOnSection || setOwnSection;
  const [data, setData]                     = useState(null);
  const [advancedData, setAdvancedData]     = useState(null);
  // Разбор брака грузим отдельно: он тяжелее остальных и нужен не на каждом
  // экране — только когда открыли «Брак».
  const [defectSrc, setDefectSrc]           = useState(null);
  const [loading, setLoading]               = useState(true);
  const [lastUpdate, setLastUpdate]         = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: adv }] = await Promise.all([
        analyticsAPI.getDashboard(period),
        advancedAPI.get(period),
      ]);
      setData(d);
      setAdvancedData(adv);
      setLastUpdate(new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
    } catch {
      toast.error(t("Не удалось загрузить данные"));
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  // Разбор брака грузим ТОЛЬКО когда открыт его раздел.
  //
  // Раньше он шёл вместе с общей загрузкой — то есть каждые две минуты и на
  // любом разделе, даже если человек смотрит заказы. Запрос тяжёлый, а
  // браузер держит к одному адресу около шести соединений: несколько таких
  // в очереди — и страница перестаёт открываться вовсе.
  // Счётчик заявок переехал в DirectorHub — туда же, где теперь живёт меню.
  // Держать его здесь стало нельзя: страница размонтируется, когда директор
  // уходит в «Простои прессов», и цифра, которая должна быть видна с ЛЮБОГО
  // экрана, вместе с ней исчезала.

  useEffect(() => {
    if (section !== 'defects') return undefined;
    let alive = true;
    // Ровно выбранный период, без «не меньше тридцати». Иначе под кнопкой
    // «Сегодня» стояли цифры за три месяца, и человек читал их как сегодняшние.
    advancedAPI.defects(period)
      .then(r => { if (alive) setDefectSrc(r.data); })
      .catch(() => { if (alive) setDefectSrc(null); });
    return () => { alive = false; };
  }, [section, period]);

  // Обновление раз в две минуты
  useEffect(() => {
    const id = setInterval(load, 120000);
    return () => clearInterval(id);
  }, [load]);

  const PERIOD_OPTIONS = [
    { value: 1,  label: 'Сегодня' },
    { value: 7,  label: '7 дней'  },
    { value: 30, label: '30 дней' },
  ];

  // Список разделов переехал в DirectorHub: он рисует их в боковом меню, и
  // название раздела теперь ровно одно на всю программу. Здесь оставалась
  // вторая копия — с другими подписями («Прессы (OEE)», «Пересортица»,
  // «Сорта»), и по ней же строилась полоса кнопок поверх содержимого.
  //
  // Заодно ушли два имени, из-за которых открывали не то: «🏷 Пересортица» и
  // «🏷 Сорта» стояли рядом, с одним значком, а показывали разное — кто менял
  // сорта и сколько какого сорта вышло.

  const BTN = {
    background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--muted)', padding: '7px 12px', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
  };

  // Разделы и период прилипают к верху.
  //
  // Таблицы здесь длинные, и чтобы сменить период или уйти в другой раздел,
  // приходилось прокручивать всё обратно наверх. Высоту шапки меряем и
  // отдаём вниз переменной: на узком экране кнопки переносятся на вторую
  // строку, и зашитое число тут же начало бы перекрывать содержимое.
  const headRef = useRef(null);
  useLayoutEffect(() => {
    const el = headRef.current;
    if (!el) return undefined;
    const apply = () => document.documentElement.style.setProperty(
      '--dir-head-h', el.offsetHeight + 'px');
    apply();
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(apply) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', apply);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', apply);
    };
  });

  // Панель периода и выгрузок — общая для всех разделов
  const toolbar = (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      gap: 12, flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', gap: 6 }}>
        {PERIOD_OPTIONS.map(opt => (
          <button key={opt.value} onClick={() => setPeriod(opt.value)} style={{
            background: period === opt.value ? 'var(--accent)' : 'var(--card)',
            border: '1px solid ' + (period === opt.value ? 'var(--accent)' : 'var(--border)'),
            borderRadius: 8, color: period === opt.value ? '#fff' : 'var(--muted)',
            padding: '7px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            fontFamily: 'inherit',
          }}>
            {t(opt.label)}
          </button>
        ))}
      </div>
      {/* Выгрузки — не на каждом экране.
          Три кнопки скачивания ходили за человеком по всем разделам и
          занимали половину шапки там, где качать нечего: в разборе брака или
          в отклонении режима они ни при чём. Оставляем их на первом разделе,
          куда и заходят за общей картиной. Обновление тут же не нужно:
          дашборд перечитывает себя сам раз в две минуты. */}
      {section === 'now' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {lastUpdate && (
            <span style={{ color: 'var(--dim)', fontSize: 12 }}>
              {t('Обновлено')}: {lastUpdate}
            </span>
          )}
          {/* Выгрузка паллет убрана вместе с паллетами: кнопка осталась бы
              качать пустой файл и обещать учёт, которого больше нет */}
          <button onClick={() => exportAPI.orders({ status: '' }).catch(() => {})} style={BTN}>
            {t('📥 Заказы')}
          </button>
          <button onClick={() => exportAPI.stock().catch(() => {})} style={BTN}>
            {t('📥 Склад')}
          </button>
          <button onClick={() => exportAPI.production({ period }).catch(() => {})} style={BTN}>
            {t('📥 Производство')}
          </button>
          <button onClick={load} disabled={loading} style={{
            ...BTN, color: loading ? 'var(--dim)' : 'var(--muted)',
            cursor: loading ? 'default' : 'pointer',
          }}>
            {loading ? '...' : '🔄'}
          </button>
        </div>
      )}
    </div>
  );

  // Объявляется ПОСЛЕ toolbar: const не поднимается, и страница падала на
  // входе — «can't access lexical declaration before initialization».
  const head = (
    <div ref={headRef} style={{
      position: 'sticky', top: 0, zIndex: 20,
      background: 'var(--bg)', paddingBottom: 12, marginBottom: 12,
      borderBottom: '1px solid var(--border)',
    }}>
      {toolbar}
    </div>
  );

  if (loading && !data) {
    return (
      <Shell title={t('Директор — Панель управления')}>
        {head}
        <div style={{ textAlign: 'center', paddingTop: 80 }}>
          <div style={{ color: 'var(--border)', fontSize: 48, marginBottom: 16 }}>📊</div>
          <p style={{ color: 'var(--mid)', fontSize: 16 }}>{t('Загрузка...')}</p>
        </div>
      </Shell>
    );
  }

  const G = { display: 'flex', flexDirection: 'column', gap: 24 };

  return (
    <Shell title={t('Директор — Панель управления')}>
      {head}

      {/* ── Что происходит: то, из-за чего панель вообще открывают ── */}
      {section === 'now' && (
        <div style={G}>
          <TopProblems       problems={advancedData?.top_problems} />
          {/* Сводный балл «здоровья» убран. Он складывался из четырёх частей,
              две из которых были нулями не потому, что плохо, а потому что
              не посчитано: брак брался из пустого поля, а срок — из заказов,
              у которых не заполнен филиал создателя. Получалось 54 балла из
              ста, и что с ними делать, не знал никто. Разбор по частям —
              рядом, в своих разделах, и там числа настоящие. */}
          <ShiftSummary />
          {data?.stock_warnings?.length > 0 && <StockWarnings warnings={data.stock_warnings} />}
        </div>
      )}

      {section === 'orders' && data && (
        <div style={G}>
          <div>
            <SectionTitle>{t('📋 Заказы')}</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: 12 }}>
              {/* Заказы, которые стоят и ждут человека, — первой карточкой.
                  Их не было на экране вовсе: статус «ожидает согласования» не
                  попадал ни в одну графу, и пять заказов вчерашнего дня
                  висели невидимо. Показываем, только когда они есть: пустая
                  красная карточка каждый день — это не тревога, а фон. */}
              {(data.orders.pending + data.orders.stock) > 0 && (
                <KpiCard icon="⏸" label="Ждут решения"
                  value={data.orders.pending + data.orders.stock} color="#ef4444"
                  sub="согласование" />
              )}
              <KpiCard icon="⏳" label="В очереди"      value={data.orders.queue}      color="#f59e0b" />
              <KpiCard icon="⚙️" label="В производстве" value={data.orders.production} color="var(--accent)" />
              <KpiCard icon="✅" label="Готов"           value={data.orders.ready}      color="#22c55e" />
              <KpiCard icon="🚛" label="Отгружен"        value={data.orders.shipped}    color="#8b5cf6" />
              <KpiCard icon="📅" label={`Создано за ${period} дн.`}
                value={data.orders.period_created} color="var(--dim)"
                sub={`${data.orders.period_shipped} отгружено`} />
            </div>
          </div>
          <OrdersChart orders={data.orders} />
        </div>
      )}

      {/* ── Выпуск: сколько сделали и на что ушло сырьё ── */}
      {section === 'output' && data && (
        <div style={G}>
          <Card>
            <SectionTitle>🏭 {t('Производство за')} {period} {t('дн.')}</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { label: 'Всего произведено', value: data.production.total_done,    unit: 'шт', color: '#22c55e' },
                { label: 'Запланировано',     value: data.production.total_planned, unit: 'шт', color: 'var(--accent)' },
                { label: 'Сессий пресса',     value: data.production.jobs_count,    unit: 'шт', color: 'var(--muted)' },
                {
                  label: 'Брак',
                  value: data.production.total_defect,
                  unit:  `шт (${data.production.defect_rate}%)`,
                  color: data.production.defect_rate > 2 ? '#ef4444' : 'var(--dim)',
                },
              ].map((row, i) => (
                <div key={i} style={{
                  background: 'var(--bg)', borderRadius: 8, padding: '10px 14px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span style={{ color: 'var(--dim)', fontSize: 13 }}>{t(row.label)}</span>
                  <span style={{ color: row.color, fontWeight: 700, fontSize: 20 }}>
                    {row.value} <span style={{ fontSize: 12, fontWeight: 400 }}>{row.unit}</span>
                  </span>
                </div>
              ))}
            </div>
          </Card>
          {/* Выпуск по дням — во всю ширину.
              Рядом стоял расход материалов, и оба сжимались до половины
              экрана: столбики за месяц слипались, а список материалов всё
              равно не помещался и уходил в прокрутку. Расход материалов
              убран — он про закупку, а не про то, как идёт производство,
              и живёт на складе. */}
          {data.daily_production.length > 0 && (
            <DailyProductionChart data={data.daily_production} period={period} />
          )}
        </div>
      )}

      {section === 'presses' && data && (
        <div style={G}>
          <div>
            <SectionTitle>⚙️ {t('Эффективность прессов (OEE) —')} {period} {t('дн.')}</SectionTitle>
            {data.presses.length === 0 ? (
              <div style={{ color: 'var(--mid)', textAlign: 'center', padding: 40 }}>
                {t('Данные о прессах не найдены')}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 }}>
                {data.presses.map(press => <PressOeeCard key={press.id} press={press} />)}
              </div>
            )}
          </div>
          {/* Подпись стояла цветом var(--border) — это цвет РАМОК, самый
              бледный в наборе. На светлом фоне строка практически не
              читалась. Обычный приглушённый текст и на кегль больше. */}
          <div style={{
            background: 'var(--bg)', borderRadius: 10, padding: '14px 16px',
            color: 'var(--muted)', fontSize: 13, lineHeight: 1.7,
            display: 'flex', gap: 22, flexWrap: 'wrap',
          }}>
            <span><b style={{ color: 'var(--accent)' }}>{t('Доступность')}</b> = раб.время / (раб. + простой)</span>
            <span><b style={{ color: '#a855f7' }}>{t('Производительность')}</b> = выполнено / запланировано</span>
            <span><b style={{ color: '#22c55e' }}>{t('Качество')}</b> = (выполнено − брак) / выполнено</span>
            <span><b style={{ color: 'var(--text)' }}>OEE</b> = Доступность × Производит-ть × Качество</span>
          </div>
          {/* Экран эффективности живёт СОСЕДНИМ разделом того же дашборда, а
              не отдельным адресом: /press-efficiency в маршрутах нет и не
              было. Неизвестный адрес ловит path="*" и отправляет на /login —
              человек нажимал кнопку и оказывался на входе, потеряв то, что
              смотрел. Ни ошибки, ни следа. */}
          <button onClick={() => (onOpen ? onOpen('pressdetail') : navigate('/director'))} style={{
            alignSelf: 'flex-start', background: '#8b5cf620', border: '1px solid #8b5cf640',
            borderRadius: 8, color: '#8b5cf6', padding: '9px 16px', cursor: 'pointer',
            fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
          }}>
            📈 {t('Подробно по каждому прессу')}
          </button>
        </div>
      )}

      {section === 'regrade' && (
        <div style={G}>
          <RegradeRequests onCount={onRegradeCount} />
        </div>
      )}

      {/* ── Брак: откуда он берётся ── */}
      {section === 'defects' && (
        <div style={G}>
          {data?.grade_breakdown?.length > 0 && <GradeBreakdownChart data={data.grade_breakdown} />}
          <DefectSources data={defectSrc} days={period} press="" />
          {/* Матрица «пресс × оператор» убрана: она тоже считала по
              quantity_defect и показывала ровные нули по всем клеткам.
              Те же два разреза есть в «Откуда берётся брак» — по настоящим
              сортам, поставленным складом. */}
        </div>
      )}

      {section === 'mode' && (
        <div style={G}><ModeDeviation days={period} /></div>
      )}

      {section === 'shifts' && (
        <div style={G}><ShiftComparison shifts={advancedData?.shift_comparison} /></div>
      )}

      {section === 'forecast' && (
        <div style={G}><CapacityForecast forecast={advancedData?.capacity_forecast} /></div>
      )}

      {section === 'supply' && (
        <div style={G}><SupplierQuality suppliers={advancedData?.supplier_quality} /></div>
      )}
    </Shell>
  );
}
